import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { theme } from "../../../../constants/StyledComponents";
import {
  ChartTooltipArrow,
  ImpactGroupGap,
  ImpactLabelGroup,
  ImpactLabelsRow,
  LabelsRowWrapper,
  LineGroup,
  StyledChartTooltip,
  StyledPerformanceChart,
  StyledThresholdsLegend,
  TooltipPositioner,
  TooltipValue,
} from "./PerformanceChart.styled";
import { LabelFormatter } from "recharts/types/component/Label";
import React, { useRef, useState } from "react";
import { TitlesTyp } from "../../../../components/Typography/Typography";
import { FontWeightEnum } from "../../../../types/AppTypes";
import { NO_DATA_CHART_LABEL } from "../../../../constants/Dictionary";
import { StyledMessageContainer } from "../../PotentialMatrixV3.styled";
import { EmptyState } from "../../../../components/EmptyState/EmptyState";
import MatrixDevelopmenetImage from ".././../../../images/empty_matrix.png";
import {
  BadgeChip,
  BadgeLabelSize,
} from "../../../../components/BadgeChip/BadgeChip";

type PerformanceGradeStat = {
  internalGrade: string;
  count: number;
};

type PerformanceRangeStat = {
  initials: string;
  name: string;
  total: number;
  grades: PerformanceGradeStat[];
};

export type PerformanceChartImpactStats = {
  impact: string;
  ranges: PerformanceRangeStat[];
};

type PerformanceChartProps = {
  data?: PerformanceChartImpactStats[];
};

const GRADE_COLOR_BY_LABEL: Record<string, string> = {
  "1 - Junior Consultant": "#DDB378",
  "2 - Consultant": "#E38EA8",
  "3 - Senior Consultant": "#B58FB3",
  "4 - Manager": "#6FAFA7",
  "5 - Senior Manager": "#6395BC",
};

const THRESHOLD_LEGEND_ITEMS = [
  { label: "L", range: "0-20 %" },
  { label: "M", range: "21-60 %" },
  { label: "MH", range: "61-80 %" },
  { label: "H", range: "81-100 %" },
];

type FlatRow = Record<string, string | number> & {
  initials: string;
  total: number;
};
interface HoveredSegment {
  x: number;
  y: number;
  value: number;
}

export const PerfomanceChart = ({ data = [] }: PerformanceChartProps) => {
  const gradeKeys = Object.keys(GRADE_COLOR_BY_LABEL);
  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const [hoveredSegment, setHoveredSegment] = useState<HoveredSegment | null>(
    null,
  );
  const hasData = data.length > 0;

  const handleBarMouseEnter =
    (gradeKey: string) =>
    (data: unknown, _index: number, event: React.MouseEvent<SVGElement>) => {
      const rect = (event.target as SVGRectElement).getBoundingClientRect();
      const containerRect = chartWrapperRef.current?.getBoundingClientRect();
      if (!containerRect) return;

      const row = data as FlatRow;
      const value = row[gradeKey];
      if (typeof value !== "number") return;

      setHoveredSegment({
        x: rect.left - containerRect.left,
        y: rect.top - containerRect.top + rect.height / 2,
        value,
      });
    };
  const handleBarMouseLeave = () => setHoveredSegment(null);

  const flattenedData: FlatRow[] = data.flatMap((impactStats) =>
    impactStats.ranges.map((rangeStat) => {
      const flatRange: FlatRow = {
        initials: rangeStat.initials,
        total: rangeStat.total,
      };
      rangeStat.grades.forEach((gradeStat) => {
        flatRange[gradeStat.internalGrade] = gradeStat.count;
      });
      return flatRange;
    }),
  );

  const formatLabel: LabelFormatter = (value) => {
    return Number(value) > 0 ? value : "";
  };

  const maxTotal = Math.max(...flattenedData.map((row) => row.total));
  const Y_AXIS_STEP = 20;

  const yAxisMax =
    Math.floor(maxTotal / Y_AXIS_STEP) * Y_AXIS_STEP + Y_AXIS_STEP * 2;

  const yAxisTicks = (() => {
    const ticks = [];
    for (let value = 0; value <= yAxisMax; value += Y_AXIS_STEP) {
      ticks.push(value);
    }
    return ticks;
  })();

  const renderTooltip = (value: number) => (
    <StyledChartTooltip>
      <TooltipValue>{value}</TooltipValue>
      <ChartTooltipArrow />
    </StyledChartTooltip>
  );

  return hasData ? (
    <>
      <StyledPerformanceChart>
        <div
          className="chart-body"
          ref={chartWrapperRef}
          onMouseLeave={handleBarMouseLeave}
        >
          {
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                accessibilityLayer
                data={flattenedData}
                barCategoryGap="10%"
                margin={{ top: 30, right: 30, left: 20, bottom: 8 }}
                barGap={4}
              >
                <CartesianGrid
                  vertical={false}
                  stroke={theme.colors.purple_2}
                />
                <XAxis
                  dataKey="initials"
                  tickLine={false}
                  axisLine={false}
                  tick={{
                    fontSize: theme.variables.fs_14,
                    fill: theme.colors.grey_1,
                  }}
                  padding={{ left: 20, right: 20 }}
                  tickFormatter={(value: string) => (value ? value : "")}
                  fontWeight={theme.variables.fw_semiBold}
                  fontFamily={theme.variables.font_family}
                />
                <YAxis
                  width={30}
                  domain={[0, yAxisMax]}
                  ticks={yAxisTicks}
                  tickLine={false}
                  axisLine={false}
                  fontSize={theme.variables.fs_12}
                  fontWeight={theme.variables.fw_Regular}
                  tick={{ fill: theme.colors.black }}
                />

                {gradeKeys.map((gradeKey, index) => {
                  const isLast = index === gradeKeys.length - 1;
                  return (
                    <>
                      <Bar
                        key={gradeKey}
                        stackId="grades"
                        dataKey={gradeKey}
                        name={gradeKey}
                        fill={GRADE_COLOR_BY_LABEL[gradeKey]}
                        isAnimationActive={false}
                        barSize={40}
                        radius={isLast ? [4, 4, 0, 0] : 0}
                        onMouseEnter={handleBarMouseEnter(gradeKey)}
                        onMouseLeave={handleBarMouseLeave}
                      >
                        {isLast && (
                          <LabelList
                            dataKey="total"
                            position="top"
                            fontSize={theme.variables.fs_16}
                            fontWeight={theme.variables.fw_semiBold}
                            fill={theme.colors.black}
                            fontFamily={theme.variables.font_family}
                            formatter={formatLabel}
                          />
                        )}
                      </Bar>
                    </>
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          }
          {hoveredSegment && (
            <TooltipPositioner $x={hoveredSegment.x} $y={hoveredSegment.y}>
              {renderTooltip(hoveredSegment.value)}
            </TooltipPositioner>
          )}
        </div>

        <LabelsRowWrapper>
          <ImpactLabelsRow>
            {data.map((impactStats, groupIndex) => {
              const isLastGroup = groupIndex === data.length - 1;
              return (
                <React.Fragment key={impactStats.impact}>
                  <ImpactLabelGroup>
                    <LineGroup />
                    <TitlesTyp fontWeight={FontWeightEnum.SEMIBOLD} variant="3">
                      {impactStats.impact}
                    </TitlesTyp>
                  </ImpactLabelGroup>
                  {!isLastGroup && <ImpactGroupGap />}
                </React.Fragment>
              );
            })}
          </ImpactLabelsRow>
        </LabelsRowWrapper>
      </StyledPerformanceChart>
      {/* TODO: i range arrivano dai config - dati dinamici */}
      <StyledThresholdsLegend>
        {THRESHOLD_LEGEND_ITEMS.map(({ label, range }) => (
          <BadgeChip
            size={BadgeLabelSize.MEDIUM}
          >{`${label} = ${range}`}</BadgeChip>
        ))}
      </StyledThresholdsLegend>
    </>
  ) : (
    <StyledMessageContainer>
      <EmptyState
        imageSrc={MatrixDevelopmenetImage}
        label={NO_DATA_CHART_LABEL}
      />
    </StyledMessageContainer>
  );
};
